// @flow
import objectToQueryString from 'mz-utils/objectToQueryString'
import omitEmptyFields from 'mz-utils/omitEmptyFields'
import request from './request'
import { config } from '../config'
import jwt from '../utils/jwt'

// Types
import type { RequestOptions } from './request'

// Utils
import {
  deleteJWTToken,
  saveJWTToken,
  getRefreshToken,
  getAuthorizationToken,
} from './credentialsStorage'

type CustomOptions = {
  jwt?: boolean,
  externallyBuiltUrl?: boolean,
  apiKey?: boolean,
}

type MethodType = (
  path: string,
  options?: ?RequestOptions,
  customs?: ?CustomOptions
) => Promise<any>

type ClientType = $ObjMap<typeof request, () => MethodType>

// Constants
export const HTTP_FORBIDDEN_ERROR = 'authentication_failed'
export const REFRESH_TOKEN_ENDPOINT = '/auth/token-refresh/'
const API_KEY_HEADER = 'Api-Key'
const AUTH_HEADER = 'Authorization'
const REF_HEADER = 'Ref'
const LANG_HEADER = 'Lang'
const DISTINCT_ID_HEADER = 'Distinct-Id'
let JWT_REFRESH_PROMISE = null
const DEFAULT_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
}

export {
  deleteJWTToken,
  saveJWTToken,
  getRefreshToken,
  getAuthorizationToken,
} from './credentialsStorage'

function redirectToLogin() {
  if (typeof window !== 'undefined') {
    const currentPath = window.location.pathname + window.location.search
    const redirectUrl = `/user/login?next=${encodeURIComponent(currentPath)}`
    window.location.href = redirectUrl
  }
}

/**
 * Ensure that currently active JWT token (located in local storage) is valid
 * and can be used to make an authorized request. Resolve a promise with new
 * headers where valid authorization token provided.
 * @param  {Object} headers
 * @param  {bool} forceEnsure
 * @return {Promise<Object>}
 */
function ensureJWTValid(headers, forceEnsure) {
  return new Promise((resolve, reject) => {
    const auth = headers.Authorization
    if (!auth || (jwt.isAuthenticated() && !forceEnsure)) {
      resolve(headers)
      return
    }

    if (!JWT_REFRESH_PROMISE) {
      JWT_REFRESH_PROMISE = refreshJWTToken()
    }

    JWT_REFRESH_PROMISE.then(() => {
      JWT_REFRESH_PROMISE = null
      resolve({
        ...headers,
        [AUTH_HEADER]: `JWT ${getAuthorizationToken()}`,
      })
    }).catch((error) => {
      JWT_REFRESH_PROMISE = null
      reject(error)
    })
  })
}

/**
 * Try to refresh authorization JWT token based on refresh token located
 * in LocalStorage. On success replace tokens in LocalStorage, on fail
 * delete all stored tokens and reject the promise
 * @return {Promise<void>}
 */
export function refreshJWTToken(): Promise<void> {
  const refreshToken = getRefreshToken()
  if (!refreshToken) {
    deleteJWTToken()
    return Promise.reject()
  }

  return Client.post(
    REFRESH_TOKEN_ENDPOINT,
    { body: { refresh_token: refreshToken } },
    { jwt: false }
  )
    .then(saveJWTToken)
    .then(({ token, refresh_token }) => {
      config.ON_REFRESH_EXPIRED_JWT &&
        config.ON_REFRESH_EXPIRED_JWT({ token, refresh_token })
    })
    .catch((error) => {
      deleteJWTToken()
      const updatedError = { ...error, requiresRedirect: true }
      throw updatedError
    })
}

/**
 * Extend provided (or not provided) headers with the headers required
 * by Mozio API, which is ref param, active language, api-key and auth token
 * @param  {Object?} overrideHeaders
 * @param  {CustomOptions} customs
 * @return {Object}
 */
function prepareHeaders(options: *, customs: CustomOptions) {
  const { headers: overrideHeaders, body } = options
  const defaultHeaders = body instanceof FormData ? {} : DEFAULT_HEADERS

  const headers = {
    ...defaultHeaders,
    ...overrideHeaders,
    [LANG_HEADER]: config.LANGUAGE,
    [REF_HEADER]: config.PARTNER_REF,
    [DISTINCT_ID_HEADER]: config.DISTINCT_ID,
  }

  const authToken = getAuthorizationToken()
  if (customs.jwt !== false && authToken) {
    headers[AUTH_HEADER] = `JWT ${authToken}`
  }

  if (customs.apiKey !== false) {
    headers[API_KEY_HEADER] = config.DEFAULT_REQUEST_PARAMS.api_key
  }

  return omitEmptyFields(headers, { string: true })
}

/**
 * Performs the request by given actual request paramters with
 * JWT token ensuring. Aim of this function is to guarantee that we did
 * everything possible to make an authorized request
 * @param  {string} method
 * @param  {string} url
 * @param  {Object} params
 * @return {Promise}
 */
function performRequest(method, url, params): Promise<any> {
  let forceRefreshedToken = false

  const validTokenHandler = (newHeaders) => {
    const newParams = { ...params, headers: newHeaders }
    return request[method](url, newParams).catch((err) => {
      if (
        err &&
        err.errorCode === HTTP_FORBIDDEN_ERROR &&
        newHeaders[AUTH_HEADER]
      ) {
        if (!forceRefreshedToken) {
          forceRefreshedToken = true
          return ensureJWTValid(newHeaders, true).then(validTokenHandler)
        }
        deleteJWTToken()
        redirectToLogin()
      }
      throw err
    })
  }

  return ensureJWTValid(params.headers)
    .then(validTokenHandler)
    .catch((error) => {
      if (error.requiresRedirect) {
        redirectToLogin()
      }
      throw error
    })
}

/**
 * Convert given raw parameters to an actual request parameters, which is
 * actual request path, actual request headers
 * @param  {string} method
 * @param  {string} path
 * @param  {RequestOptions} options
 * @param  {CustomOptions} customs
 * @return {Promise}
 */
function makeRequest(
  method: *,
  path: string,
  options?: *,
  customs?: ?CustomOptions
): Promise<any> {
  const actualOptions = options || {}
  const actualCustoms = customs || {}

  const params = {
    ...actualOptions,
    headers: prepareHeaders(actualOptions, actualCustoms),
    credentials: 'omit',
  }

  const queryPart = objectToQueryString(actualOptions.query || {})
  const url = !actualCustoms.externallyBuiltUrl
    ? `${actualOptions.baseUrl || config.BASE_URL_V2}${path}${queryPart}`
    : path

  return performRequest(method, url, params)
}

/**
 * Wrap all available request methods with the app SDK specific logic,
 * like adding required headers, JWT ensuring, etc.
 */
const Client: ClientType = (Object.keys(request).reduce(
  (client, method) => ({
    ...client,
    [method]: (...args) => makeRequest(method, ...args),
  }),
  {}
): any)

export default Client
